// src/config/plans.js
// Crewboard Pricing Plans — Single source of truth for mobile + web
//
// ─── FREE LAUNCH MODE ────────────────────────────────────────────────────────
// true  → no payments processed, feature gates still enforced so hirers
//         understand Basic vs Pro — but upgrade buttons show "Coming Soon"
// false → Stripe checkout enabled, Basic hirers hit hard gates
export const FREE_LAUNCH_MODE = true;
// ─────────────────────────────────────────────────────────────────────────────

// ─── DRIVER PLANS ────────────────────────────────────────────────────────────
// Version 1: Drivers are always free — no subscriptions.
// Version 2: Add-ons (Profile Boost, Priority Applicant) sold à la carte via Stripe.
export const DRIVER_PLANS = {
  free: {
    id:    'free',
    name:  'Free',
    price: 0,
    label: 'Always Free',
    color: '#A3A3A3',
    features: [
      'Create verified CDL profile',
      'Get verified badge after admin review',
      'Apply to unlimited jobs',
      'Receive hire requests',
      'In-app messaging with hirers',
      'Ratings & reviews',
      'Upload Driver\'s Abstract',
    ],
    limits: {
      jobApplicationsPerMonth: Infinity, // unlimited — no cap on free
      featuredInSearch:        false,    // V2 add-on: Profile Boost
      priorityApplicant:       false,    // V2 add-on: Priority Applicant Placement
    },
  },
};

// ─── DRIVER ADD-ONS (V2 — data only, Stripe not wired yet) ───────────────────
export const DRIVER_ADDONS = {
  boost_7:  { id: 'boost_7',  icon: '🚀', name: 'Profile Boost',             duration: '7 days',  price: 7.99,  description: 'Float above non-boosted drivers in hirer search results' },
  boost_14: { id: 'boost_14', icon: '🚀', name: 'Profile Boost',             duration: '14 days', price: 12.99, description: 'Float above non-boosted drivers in hirer search results' },
  boost_30: { id: 'boost_30', icon: '🚀', name: 'Profile Boost',             duration: '30 days', price: 19.99, description: 'Float above non-boosted drivers in hirer search results' },
  priority_7:  { id: 'priority_7',  icon: '📋', name: 'Priority Applicant Placement', duration: '7 days',  price: 5.99,  description: 'Your applications appear higher in hirer applicant lists' },
  priority_14: { id: 'priority_14', icon: '📋', name: 'Priority Applicant Placement', duration: '14 days', price: 9.99,  description: 'Your applications appear higher in hirer applicant lists' },
  priority_30: { id: 'priority_30', icon: '📋', name: 'Priority Applicant Placement', duration: '30 days', price: 14.99, description: 'Your applications appear higher in hirer applicant lists' },
};

// ─── HIRER PLANS ─────────────────────────────────────────────────────────────
export const HIRER_PLANS = {
  basic: {
    id:    'basic',
    name:  'Basic',
    price: 0,
    label: 'Free Forever',
    color: '#A3A3A3',
    badge: null,
    includes: [
      'Post up to 3 jobs per month',
      'Browse driver profiles (name, photo, ratings)',
      'View CDL verified badge',
      'Receive and review job applications',
      'In-app messaging with applicants only',
      'Leave reviews after a job',
      'Business verification submission',
    ],
    notIncluded: [
      'Search Drivers (direct outreach)',
      'Advanced filters',
      'Saved driver pools',
      'More than 3 job posts/month',
      'Priority applicant sorting',
      'Priority support',
    ],
    limits: {
      jobPostsPerMonth:       3,
      searchDrivers:          false,
      advancedFilters:        false,
      savedDriverPools:       false,
      savedDriverPoolsMax:    0,
      priorityApplicants:    false,
      prioritySupport:       false,
      featuredJobPosts:      false,
      paymentBadge:          false,
    },
  },

  pro_monthly: {
    id:    'pro_monthly',
    name:  'Pro',
    price: 159,
    label: '$159 / month CAD',
    color: '#F97316',
    badge: 'MOST POPULAR',
    stripeMonthlyPriceId: 'price_PLACEHOLDER_monthly', // replace when Stripe ready
    includes: [
      'Everything in Basic',
      'Unlimited job posts',
      'Full Search Drivers access',
      'Advanced filters (endorsements, availability, rating)',
      'Save up to 5 driver pools',
      'Verified Hirer badge on profile & job posts',
      'Priority applicant sorting',
      'Priority support (24hr response)',
      'Payment method badge on profile',
    ],
    notIncluded: [],
    limits: {
      jobPostsPerMonth:       Infinity,
      searchDrivers:          true,
      advancedFilters:        true,
      savedDriverPools:       true,
      savedDriverPoolsMax:    5,
      priorityApplicants:    true,
      prioritySupport:       true,
      featuredJobPosts:      true,
      paymentBadge:          true,
    },
  },

  pro_yearly: {
    id:    'pro_yearly',
    name:  'Pro',
    price: 1699,
    label: '$1,699 / year CAD',
    color: '#F97316',
    badge: 'BEST VALUE — Save 11%',
    stripeYearlyPriceId: 'price_PLACEHOLDER_yearly', // replace when Stripe ready
    monthlyEquivalent: 141.58,
    includes: [
      'Everything in Basic',
      'Unlimited job posts',
      'Full Search Drivers access',
      'Advanced filters (endorsements, availability, rating)',
      'Save up to 5 driver pools',
      'Verified Hirer badge on profile & job posts',
      'Priority applicant sorting',
      'Priority support (24hr response)',
      'Payment method badge on profile',
    ],
    notIncluded: [],
    limits: {
      jobPostsPerMonth:       Infinity,
      searchDrivers:          true,
      advancedFilters:        true,
      savedDriverPools:       true,
      savedDriverPoolsMax:    5,
      priorityApplicants:    true,
      prioritySupport:       true,
      featuredJobPosts:      true,
      paymentBadge:          true,
    },
  },
};

// ─── HIRER ADD-ONS ───────────────────────────────────────────────────────────
export const HIRER_ADDONS = {
  featured_job: {
    id:          'featured_job',
    icon:        '📌',
    name:        'Featured Job Post',
    price:       19.99,
    label:       '$19.99 CAD per post',
    description: 'Pin your job to the top of listings for 7 days',
    requiresPro: false, // available to any paid hirer — in V2
  },
};

// ─── OFFER CODE TYPES ────────────────────────────────────────────────────────
// Used by admin to create promo codes. Stored in offerCodes Firestore collection.
export const OFFER_CODE_TYPES = {
  free_period:  'free_period',   // N months of Pro at $0
  percent_off:  'percent_off',   // X% off first N months
  fixed_off:    'fixed_off',     // $X off first payment
};

// ─── PLAN HELPER FUNCTIONS ───────────────────────────────────────────────────

// Get the active plan config for a hirer user doc
export const getHirerPlan = (userDoc) => {
  if (!userDoc) return HIRER_PLANS.basic;
  const planId = userDoc.planId || 'basic';
  // Pro plan is active only if subscriptionActive = true OR FREE_LAUNCH_MODE
  if (planId === 'basic') return HIRER_PLANS.basic;
  if (planId === 'pro_monthly' || planId === 'pro') return HIRER_PLANS.pro_monthly;
  if (planId === 'pro_yearly') return HIRER_PLANS.pro_yearly;
  return HIRER_PLANS.basic;
};

// Check if a hirer has access to a specific feature
// promoActive = has an active promotionRedemption giving Pro access
export const hirerCan = (userDoc, feature, promoActive = false) => {
  if (FREE_LAUNCH_MODE) {
    // During free launch: all features unlocked EXCEPT we still show plan UI
    // so hirers understand what Basic vs Pro is
    return true;
  }
  if (promoActive) return true; // offer code grants Pro access
  const plan = getHirerPlan(userDoc);
  return plan.limits[feature] ?? false;
};

// Check if hirer is at their job post limit
export const hirerAtJobLimit = (userDoc, promoActive = false) => {
  if (FREE_LAUNCH_MODE) return false;
  if (promoActive) return false;
  const plan = getHirerPlan(userDoc);
  const limit = plan.limits.jobPostsPerMonth;
  if (limit === Infinity) return false;
  return (userDoc?.jobPostsThisMonth || 0) >= limit;
};

// Friendly plan display name
export const planDisplayName = (planId) => {
  if (!planId || planId === 'basic') return 'Basic';
  if (planId.startsWith('pro')) return 'Pro';
  return 'Basic';
};

export const DISCLAIMER = "Crewboard facilitates connections between drivers and hirers. Payment terms, working conditions, and employment agreements are solely between the parties.";